import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges } from '@angular/core';

import { ParticipantMarketType, ParticipantPriceType, ParticipantStatus, ParticipantType } from '@cds/betting-offer';
import { EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import {
    FixtureParticipantPriceMap,
    HorsesRaceResultedViewModel,
    RaceResultedPlacedParticipant,
    V3FixtureParticipantPriceMap,
    WinGameFixture,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';
import { isEmpty } from 'lodash-es';

import { HorsesFixtureParticipantHelper } from '../../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { RacingService } from '../../../services/racing-service';
import { ResultedDividentsCalculatorService } from '../../../services/resulted-dividents-calculator.service';

@Component({
    template: '',
    standalone: false,
})
export abstract class RaceResultedComponent implements OnChanges {
    @Input() fixture: WinGameFixture;
    @Input() isGreyhounds = false;

    @HostBinding('class') className = 'horses-race-resulted';

    prices: Nullable<FixtureParticipantPriceMap> = null;
    optionPrices: Nullable<V3FixtureParticipantPriceMap> = null;
    viewModel: HorsesRaceResultedViewModel;
    optionGroup: EventOptionGroup;
    @Input() greyhoundTrapDogMapping = {};

    constructor(
        protected horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        protected resultedDividentsCalculatorService: ResultedDividentsCalculatorService,
        protected racingService: RacingService,
        public sitecore: Sitecore,
    ) {}

    abstract getNonRunners(): WinGameParticipant[];

    ngOnChanges(changes: ISimpleChanges<RaceResultedComponent>): void {
        if (changes.fixture && changes.fixture.currentValue) {
            this.setModel(changes.fixture.currentValue);
        }
    }

    protected setModel(fixture: WinGameFixture): void {
        if (this.fixture.isOptionBet && this.optionGroup) {
            this.optionPrices = this.horsesFixturePricingHelper.getOptionPrices(this.fixture, this.optionGroup, ParticipantPriceType.Fixed);
        } else {
            const prices = this.horsesFixturePricingHelper.getPrices(fixture, ParticipantMarketType.Win);
            this.prices = !isEmpty(prices) ? prices : null;
        }
        this.viewModel = this.getHorsesRaceResultedViewModel(fixture);
    }

    protected buildWinGameParticipant(rankedParticipants: WinGameParticipant[]): WinGameParticipant[] {
        let favouriteParticipants: WinGameParticipant[] = [];
        if (this.fixture.isOptionBet) {
            favouriteParticipants = this.horsesFixtureParticipantHelper.favouriteResultedOptionParticipants(this.fixture);
        } else {
            favouriteParticipants = this.horsesFixtureParticipantHelper.getResultedFavourites(this.fixture);
        }

        return rankedParticipants.map((participant) => {
            const result = {} as RaceResultedPlacedParticipant;
            const findIndex = favouriteParticipants.findIndex((p) => p.participantId === participant.participantId);
            if (findIndex > -1) {
                result.isFavourite = true;
                result.favouriteLabel = favouriteParticipants[findIndex].favouriteRank!;
            }
            if (participant.properties) {
                participant.properties.trainer = this.racingService.getRacingInfo(participant).trainer ?? '';
            }
            return {
                ...participant,
                ...result,
                attributes: this.isGreyhounds ? this.racingService.getTrapInfo(participant) : this.racingService.getRacingInfo(participant),
            };
        });
    }

    protected getPlacedParticipants(): WinGameParticipant[] {
        return this.buildWinGameParticipant(this.horsesFixtureParticipantHelper.getPlacedParticipants(this.fixture));
    }

    getHorsesRaceResultedViewModel(fixture: WinGameFixture): HorsesRaceResultedViewModel {
        const dividends = this.resultedDividentsCalculatorService.getDividends(fixture);
        return {
            placed: this.getPlacedParticipants(),
            favorites: this.fixture.isOptionBet ? this.getOptionFavoutites() : this.getFavorite(),
            nonRunners: this.getNonRunners(),
            dividends,
            hasDividends: dividends.some((dividend) => dividend.available),
            hasSilks: this.horsesFixtureParticipantHelper.areSilksAvailable(this.fixture),
            participantOptionGroups: this.horsesFixtureParticipantHelper.buildWinParticipantOptionGroups(this.fixture),
        };
    }

    getOptionFavoutites() {
        return this.horsesFixtureParticipantHelper.favouriteResultedOptionParticipants(this.fixture);
    }

    getFavorite(): WinGameParticipant[] {
        return this.horsesFixtureParticipantHelper.getResultedFavourites(this.fixture);
    }
}

@Component({
    selector: 'ms-greyhounds-race-resulted',
    templateUrl: './greyhounds-race-resulted.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class GreyhoundsRaceResultedComponent extends RaceResultedComponent implements OnChanges {
    override optionGroup: EventOptionGroup;
    placeTerms: number | null;
    constructor(
        horsesFixturePricingHelper: HorsesFixturePricingHelper,
        horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        resultedDividentsCalculatorService: ResultedDividentsCalculatorService,
        racingService: RacingService,
        sitecore: Sitecore,
    ) {
        super(horsesFixturePricingHelper, horsesFixtureParticipantHelper, resultedDividentsCalculatorService, racingService, sitecore);
    }

    override ngOnChanges(changes: ISimpleChanges<RaceResultedComponent>): void {
        if (changes.fixture && changes.fixture.currentValue) {
            if (this.fixture.isOptionBet) {
                this.optionGroup = this.fixture.optionGroups[0];
                this.placeTerms = this.horsesFixtureParticipantHelper.getOptionMarketPlaceTerms(this.optionGroup);
            }
            this.setModel(changes.fixture.currentValue);
        }
    }

    override getPlacedParticipants(): WinGameParticipant[] {
        const rankedParticipants = this.horsesFixtureParticipantHelper.getPlacedParticipants(this.fixture).map((participant) => {
            if (participant.properties) {
                participant.properties.trainer = this.racingService.getRacingInfo(participant).trainer ?? '';
            }
            return participant;
        });

        if (this.fixture.isOptionBet) {
            return rankedParticipants.filter((r) => r.properties?.type === ParticipantType.Dog);
        }
        return rankedParticipants;
    }

    override getNonRunners(): WinGameParticipant[] {
        let nonRunners = this.horsesFixtureParticipantHelper.getNonRunnersList(this.fixture);
        if (this.fixture.isOptionBet) {
            nonRunners = this.horsesFixtureParticipantHelper.removeTrapNonrunners(nonRunners);
            nonRunners.map((r) => {
                if (r.properties?.type === ParticipantType.Trap && r.status === ParticipantStatus.NonRunner) {
                    r.name.value = this.sitecore.greyhoundRacing.messages.VacantGreyHound;
                }
            });
        }
        return nonRunners;
    }
}
